/**
 * Utilitários e componentes de UI para o Visualizador de Páginas.
 *
 * - normalizarPagina: converte fonte_pagina heterogêneo (p-066, 8, llm, 0…)
 *   no número da página, ou null. Espelha core/paginas.py (validado no backend).
 * - parseIntervalo: interpreta "2;7;9" / "5-9" numa lista de páginas.
 * - ChipPagina: botão "🔎 pág. N" clicável (ou "—" quando não há página).
 * - DigitarPaginas: campo estilo caixa de impressão.
 */
import { Search } from "lucide-react";
import { useState } from "react";

const RE_PAGINA_PREFIXADA = /p-?(\d+)/i;
const SEM_PAGINA = new Set(["llm", "ia", "n/a", "na", "-", "?", ""]);

/** Converte um fonte_pagina heterogêneo no número da página, ou null. */
export function normalizarPagina(fonte: string | number | null | undefined): number | null {
  if (fonte == null) return null;
  if (typeof fonte === "number") return fonte > 0 ? fonte : null;
  const texto = String(fonte).trim();
  if (!texto || SEM_PAGINA.has(texto.toLowerCase())) return null;
  const m = RE_PAGINA_PREFIXADA.exec(texto);
  if (m) {
    const n = parseInt(m[1], 10);
    return n > 0 ? n : null;
  }
  if (/^\d+$/.test(texto)) {
    const n = parseInt(texto, 10);
    return n > 0 ? n : null;
  }
  const m2 = /(\d+)/.exec(texto);
  if (m2) {
    const n = parseInt(m2[1], 10);
    return n > 0 ? n : null;
  }
  return null;
}

/** Interpreta "2;7;9" / "5-9" / "2;5-7" numa lista de páginas (sem duplicatas). */
export function parseIntervalo(texto: string, maximo?: number): number[] {
  if (!texto) return [];
  const resultado: number[] = [];
  const vistos = new Set<number>();
  const add = (n: number) => {
    if (n < 1 || (maximo != null && n > maximo)) return;
    if (!vistos.has(n)) {
      vistos.add(n);
      resultado.push(n);
    }
  };
  for (const token of texto.trim().split(/[;,\s]+/)) {
    if (!token) continue;
    if (token.includes("-")) {
      const [a, b] = token.split("-", 2);
      let ini = parseInt(a, 10);
      let fim = parseInt(b, 10);
      if (Number.isNaN(ini) || Number.isNaN(fim)) continue;
      if (ini > fim) [ini, fim] = [fim, ini];
      for (let n = ini; n <= fim; n++) add(n);
    } else {
      const n = parseInt(token, 10);
      if (!Number.isNaN(n)) add(n);
    }
  }
  return resultado;
}

/**
 * Chip "🔎 pág. N". Se `fonte` não tiver página identificável, mostra um
 * traço discreto (não clicável) — não inventa página.
 */
export function ChipPagina({
  fonte,
  onAbrir,
  rotulo,
}: {
  fonte: string | number | null | undefined;
  onAbrir: (pagina: number) => void;
  /** Texto opcional antes do número (padrão "pág."). */
  rotulo?: string;
}) {
  const pagina = normalizarPagina(fonte);
  if (pagina == null) {
    return (
      <span className="text-slate-300" title="Página não identificada">
        —
      </span>
    );
  }
  return (
    <button
      type="button"
      onClick={() => onAbrir(pagina)}
      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-indigo-200 bg-indigo-50 text-indigo-700 text-xs font-medium hover:bg-indigo-100 hover:border-indigo-300 transition-colors"
      title={`Ver a página ${pagina} do balancete`}
    >
      <Search size={11} />
      {rotulo ?? "pág."} {pagina}
    </button>
  );
}

/**
 * Campo "digitar páginas" (estilo caixa de impressão): aceita ; para avulsas
 * e - para intervalos. Ex.: 2;7;9 ou 5-9. Abre o visualizador no conjunto.
 */
export function DigitarPaginas({
  max,
  onAbrir,
}: {
  /** Total de páginas (para validar). Opcional. */
  max?: number;
  onAbrir: (paginas: number[]) => void;
}) {
  const [texto, setTexto] = useState("");
  const paginas = parseIntervalo(texto, max);
  const invalido = texto.trim().length > 0 && paginas.length === 0;

  const abrir = () => {
    if (paginas.length > 0) onAbrir(paginas);
  };

  return (
    <div className="inline-flex items-center gap-2">
      <div className="flex flex-col">
        <div className="inline-flex items-center gap-1.5">
          <input
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") abrir();
            }}
            placeholder="ex.: 2;7;9 ou 5-9"
            className={`w-36 px-2 py-1.5 text-xs rounded-md border ${
              invalido ? "border-rose-300 bg-rose-50" : "border-slate-300"
            } focus:outline-none focus:ring-1 focus:ring-indigo-400`}
            title="Digite as páginas: ; separa avulsas, - faz intervalo"
          />
          <button
            type="button"
            onClick={abrir}
            disabled={paginas.length === 0}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded-md bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Search size={13} /> Ver páginas
          </button>
        </div>
        {invalido && (
          <span className="text-[10px] text-rose-600 mt-0.5">
            Não entendi. Use ; para separar (2;7;9) e - para intervalo (5-9).
          </span>
        )}
      </div>
    </div>
  );
}
